import React, { FC, useEffect, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import styled, { ThemeProvider } from 'styled-components';
import { Route, Switch, useHistory } from 'react-router-dom';
import { reportPageView } from '@api/activityLogApi';

import './polyfills';
import './config/reactotronConfig';

import CalendarLoader from './components/calendar/CalendarLoader.loader';
import ConfirmationDialog from './components/common/dialog/ConfirmationDialog';
import MainNavigation from './components/navigation/MainNavigation';
import { FlexColumn, Row } from './components/common/layoutUtils';
import Login from './components/auth/Login.loader';
import ForgotPassword from './components/auth/ForgotPassword.loader';
import * as routes from './utils/constants/routes';
import ResetPassword from './components/auth/ResetPassword.loader';
import LinkExpired from './components/auth/LinkExpired.loader';
import { bindActionCreators, Dispatch } from 'redux';
import { commonActions } from './state/common';
import Settings from './components/settings/Settings.loader';
import { RootState } from '@state/types';
import {
  getAppLoaded,
  getIsCoachSession,
  getParentLink,
  getSubscriptionExpired,
  getVerificationRequired
} from './state/common/selectors';
import ConditionalRoutes from './ConditionalRoutes';
import ConnectAiAppPage from './components/connect/ConnectAiAppPage';
import { hasPendingAgentConnect } from './utils/pendingAgentConnect';
import Charts from './components/charts/Charts.loader';
import ManageTaskDialog from './components/task/manage/ManageTaskDialog';
import CongratsMobile from './components/auth/CongratsMobile.loader';
import { isMobile, isMobileApp, isTouchScreen } from './utils/screenUtils';
import GpaCalculator from './components/gpaCalculator/GpaCalculator.loader';
import NotFound from '@components/common/pages/NotFound.loader';
import BrowserNotSupported from '@components/common/pages/BrowserNotSupported.loader';
import MaintenancePage from '@components/common/pages/MaintenancePage.loader';
import OfflinePage from '@components/common/pages/OfflinePage';
import ServerErrorPage from '@components/common/pages/ServerErrorPage.loader';
import { darkThemeOn, lightThemeOn } from './assets/themes';
import { freeTimeColors } from '@utils/theme/freeTimeColor';
import { darkBackground, softBackground } from './utils/theme/softBackground';
import { DatePickerStyles } from './components/common/inputs/Input.styles';
import { useThemeMode } from './utils/theme/themeMode';
import { reloadAfterAgentChange, useAgentChangeRefresh } from '@hooks/useAgentChangeRefresh';
import { setAppLoaded, setRefreshData, useConnection } from '@utils/maintenance';
import { useNewDayRefresh } from '@hooks/useNewDayRefresh';
import { useTimerTabTitle } from '@hooks/useTimerTabTitle';
import { useSessionPingClicks } from '@hooks/useSessionPingClicks';
import { useTimerAlerts } from '@hooks/useTimerAlerts';
import TasksDialog from './components/task/TasksDialog';
import StudyTime from './components/calendar/StudyTime.loader';
import AwakeTime from './components/calendar/AwakeTime.loader';
import SetupOnboarding from './components/onboardingSetup/quickSetup/SetupOnboarding.loader';
import ExpiredMembershipPage from './components/settings/membership/ExpiredMembershipPage.loader';
import Badges from './components/settings/Badges.loader';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { googleRecaptchaKey, urls } from './config/configConstants';
import colors from './utils/colors';
import { RegularText } from './utils/typography';
import t from './i18n/t';
import { SemesterUser } from 'shovel-lib/types';
import { getCurrentUser, getId } from './state/semester/selectors';
import { authenticationApi, timeUtils } from 'shovel-lib';
import { alert, NotificationMode } from '@components/common/snackbarNotifications/Notifications';
import { getErrorMessage } from './api/network';
import SignUpOrganization from './components/auth/SignUpOrganization.loader';
import VerifyEmail from './components/auth/VerifyEmail.loader';
import CalendarPage from './components/mobile/CalendarPage.loader';
import LinkGoogleCalToCourseDialog from './components/common/googleCalendar/LinkGoogleToCourseDialog';
import OnboardingDemo from './components/onboardingSetup/demo/OnboardingDemo.loader';
import EditorPage from './components/mobile/EditorPage.loader';
import { CalendarMode } from './state/common/types';
import Commitments from './components/calendar/Commitments.loader';
import PersonalizationDialog from '@components/settings/personalization/PersonalizationDialog';
import ParentLinkPage from './components/settings/membership/ParentLinkPage.loader';
import SuccessPaymentPage from './components/settings/membership/SuccessPaymentPage.loader';
import { rawColors } from './utils/theme/rawColors';
import TaskListPage from './components/calendar/TaskListPage.loader';
import ImportCourseSyllabusFilePickerDialog from './components/course/ImportCourseSyllabusFilePickerDialog';
import CreateTaskOverlay from './components/task/CreateTaskOverlay';
import RepeatPatternDialog from './components/task/createTask/RepeatPatternDialog';
import NewEstimateDialog from './components/task/manage/partials/NewEstimateDialog';
import CategoryTimeDialog from './components/task/categoryTime/CategoryTimeDialog';
import { timerActions } from './state/timer/index';
import MobileSignUpInstructions from './components/auth/MobileSignUpInstructions';
import ResendActivationEmail from './components/auth/ResendActivationEmail';
import IcsUpdatesDialog from './components/calendar/dialogs/IcsUpdatesDialog';
import { getIcsUpdatesTimestamp } from './state/ics/selectors';
import { icsActions } from './state/ics';
import { authActions } from './state/auth';
import './assets/css/styles.css';
import './assets/css/datepicker.css';
import 'rc-slider/assets/index.css';
import SignUpMobileInfo from '@components/auth/SignUpMobileInfo';
import { ApiErrorResponse } from 'apisauce';
import InitCoachSession from '@components/coach/InitCoachSession';
import CoachSessionBar from '@components/coach/CoachSessionBar';
import MobileRedirectToAppOrPlayStore from '@components/mobile/MobileRedirectToAppOrPlayStore';
import SyllabusImportStatus from '@components/course/SyllabusImportStatus';
import UndoShortcut from '@components/common/UndoShortcut';
import ShovelAssistant from '@components/assistant/ShovelAssistant';

type PropsFromState = {
  appLoaded: boolean;
  subscriptionExpired: boolean;
  verificationRequired: boolean;
  user?: SemesterUser;
  semesterId?: number;
  isParentLink: boolean;
  icsUpdateTimestamp?: string;
  isCoachSession: boolean;
};

type PropsFromDispatch = {
  onAppStart: typeof commonActions.onAppStart;
  getCurrentTimer: typeof timerActions.getCurrentTimer.request;
  checkIcsUpdates: typeof icsActions.checkForIcsUpdates;
  logout: typeof authActions.logout;
};

type Props = PropsFromState & PropsFromDispatch;

const checkTimer = (getCurrentTimer: any) => {
  getCurrentTimer();
};

const App: FC<Props> = ({
  onAppStart,
  appLoaded,
  subscriptionExpired,
  verificationRequired,
  user,
  icsUpdateTimestamp,
  isParentLink,
  isCoachSession,
  getCurrentTimer,
  checkIcsUpdates,
  logout
}) => {
  const history = useHistory();

  // Shovel's activity log: which pages students visit (admin Usage page).
  useEffect(() => {
    reportPageView(history.location.pathname);
    return history.listen(location => reportPageView(location.pathname));
  }, [history]);

  useEffect(() => {
    onAppStart({ location: history.location });
  }, []);

  // After a request got no answer and Shovel turned out fine (utils/maintenance.ts), what failed is read again.
  const dispatch = useDispatch();
  useEffect(() => setRefreshData(() => reloadAfterAgentChange(dispatch)), [dispatch]);
  useEffect(() => setAppLoaded(appLoaded), [appLoaded]);
  const { offline } = useConnection();

  useEffect(() => {
    checkTimer(getCurrentTimer);
    // const interval = setInterval(() => checkTimer(getCurrentTimer), 10000);
    // return () => {
    //   clearInterval(interval);
    // };
  }, []);

  useEffect(() => {
    if (!icsUpdateTimestamp) return;

    const now = timeUtils.now();
    const nextUpdate = timeUtils.toMomentDate(icsUpdateTimestamp).add(1, 'day');
    const timeout = setTimeout(() => checkIcsUpdates(), Math.max(nextUpdate.diff(now, 'milliseconds'), 0));
    return () => {
      clearTimeout(timeout);
    };
  }, [icsUpdateTimestamp]);

  // What a student's AI app changes shows up here on its own.
  useAgentChangeRefresh(
    appLoaded &&
      !!user &&
      !subscriptionExpired &&
      !verificationRequired &&
      !isParentLink &&
      !isCoachSession &&
      !isMobile &&
      !isMobileApp
  );
  // "Today" and "Tomorrow" move on at midnight on a page left open.
  useNewDayRefresh(appLoaded && !!user && !subscriptionExpired && !verificationRequired);
  // A running timer's time in the tab's title while Shovel is out of view.
  useTimerTabTitle();
  // And a chime and desktop notice when its time is up.
  useTimerAlerts();
  useSessionPingClicks();

  const { theme } = useThemeMode();
  const background = useSelector((state: RootState) => state.settings.userSettings?.background);
  const darkKey = useSelector((state: RootState) => state.settings.userSettings?.darkBackground);
  const light = useMemo(() => lightThemeOn(softBackground(background)), [background]);
  const dark = useMemo(() => darkThemeOn(darkBackground(darkKey)), [darkKey]);
  const surface = theme === 'light' ? light.background : dark.background;
  const border = theme === 'light' ? light.borderColor : dark.borderColor;
  const darkSurface = darkBackground(darkKey);
  const savedFreeTimeColor = useSelector((state: RootState) => state.settings.userSettings?.freeTimeColor);
  const currentTheme = useMemo(() => {
    const base = theme === 'light' ? light : dark;
    return { ...base, ...freeTimeColors(savedFreeTimeColor, base.background) };
  }, [theme, light, dark, savedFreeTimeColor]);

  // For the plain CSS outside styled-components (menus, the calendar's popover), which can't read the theme.
  useEffect(() => {
    const style = document.body.style;
    style.setProperty('--color-surface', surface);
    style.setProperty('--color-bottom-border', border);
    style.setProperty('--color-theme-background-dark', darkSurface.menu ?? darkSurface.background);
  }, [surface, border, darkSurface]);

  if (!appLoaded) {
    // Opened with no internet, the app can't load: say so instead of an empty page.
    return offline ? (
      <ThemeProvider theme={currentTheme}>
        <Container>
          <OfflinePage />
        </Container>
      </ThemeProvider>
    ) : null;
  }

  if (subscriptionExpired) {
    return (
      <ThemeProvider theme={currentTheme}>
        <Container>
          <Switch>
            {/* An AI app's connect link still opens, to say why it can't connect yet. */}
            <Route path={routes.CONNECT_AI_APP} component={ConnectAiAppPage} />
            <Route
              path={routes.EXPIRED_SUBSCRIPTION}
              render={props => {
                return <ExpiredMembershipPage {...props} />;
              }}
            />
          </Switch>
        </Container>
      </ThemeProvider>
    );
  }

  if (verificationRequired) {
    return (
      <ThemeProvider theme={currentTheme}>
        <Container>
          <Switch>
            <Route path={routes.CONNECT_AI_APP} component={ConnectAiAppPage} />
            <Route path={routes.VERIFY_EMAIL} component={VerifyEmail} />
          </Switch>
        </Container>
      </ThemeProvider>
    );
  }

  if (isParentLink) {
    return (
      <ThemeProvider theme={currentTheme}>
        <Container>
          <Route path={routes.PARENT_LINK} component={ParentLinkPage} />
        </Container>
      </ThemeProvider>
    );
  }

  if (isMobileApp || isMobile) {
    return (
      <ThemeProvider theme={currentTheme}>
        <Container>
          <Switch>
            <Route path={routes.MAINTENANCE} component={MaintenancePage} />
            <Route path={routes.PAYMENT_SUCCESS} component={SuccessPaymentPage} />
            <Route path={routes.SIDEBAR} component={Commitments} />
            <Route path={routes.EDITOR} component={EditorPage} />
            {/*<ConditionalRoutes path={routes.MOBILE_ONLY_PRIVATE_ROUTE_PREFIX} isPrivate={true}>*/}
            <Route path={routes.MOBILE_CALENDAR} component={CalendarPage} />
            {isMobileApp && <Route path={routes.TERM} component={SetupOnboarding} />}
            {/*</ConditionalRoutes>*/}
            <Route path={routes.CONGRATS_MOBILE} component={CongratsMobile} />
            <Route path={routes.MOBILE_FORGOT_PASSWORD} component={ForgotPassword} />
            <Route path={routes.RESET_PASSWORD} component={ResetPassword} />
            <Route path={routes.LINK_EXPIRED} component={LinkExpired} />
            <Route path={routes.TERM} component={SetupOnboarding} />
            <Route path={routes.MOBILE_SIGNUP_INSTRUCTIONS} component={MobileSignUpInstructions} />
            <Route path={routes.RESEND_ACTIVATION_EMAIL} component={ResendActivationEmail} />
            <Route
              path={routes.SIGN_UP_ORGANIZATION}
              render={props => (
                <GoogleReCaptchaProvider reCaptchaKey={googleRecaptchaKey}>
                  <SignUpOrganization {...props} />
                </GoogleReCaptchaProvider>
              )}
            />
            <Route exact path={routes.SIGN_UP} component={SignUpMobileInfo} />
            {/*<Route path={'/'} component={AccessScreenMobile} />*/}
            <Route path={routes.MOBILE_REDIRECT_TO_APP_OR_PLAY_STORE} component={MobileRedirectToAppOrPlayStore} />
            {/* Students connecting an AI app from their phone (ChatGPT or Claude's app) approve it here, signing
                in first if needed; the phone web app has no other login. */}
            <Route path={routes.CONNECT_AI_APP} component={ConnectAiAppPage} />
            <Route path={routes.LOGIN} render={props => (hasPendingAgentConnect() ? <Login {...props} /> : null)} />
          </Switch>
        </Container>
        <ConfirmationDialog />
        <LinkGoogleCalToCourseDialog />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={currentTheme}>
      <Container>
        <Switch>
          <Route path={routes.BROWSER_NOT_SUPPORTED} component={BrowserNotSupported} />
          <Route path={routes.MAINTENANCE} component={MaintenancePage} />
          <Route path={routes.SERVER_ERROR} component={ServerErrorPage} />
          <Route path={routes.PAYMENT_SUCCESS} component={SuccessPaymentPage} />
          <Route path={routes.INIT_COACH_SESSION} component={InitCoachSession} />
          <Route path={routes.MOBILE_REDIRECT_TO_APP_OR_PLAY_STORE} component={MobileRedirectToAppOrPlayStore} />
          <Route path={routes.CONNECT_AI_APP} component={ConnectAiAppPage} />
          <ConditionalRoutes path={routes.AUTH_ROUTE_PREFIX} isPrivate={false}>
            <Switch>
              <Route path={routes.LOGIN} component={Login} />
              <Route path={routes.FORGOT_PASSWORD} component={ForgotPassword} />
              <Route path={routes.RESET_PASSWORD} component={ResetPassword} />
              <Route path={routes.RESEND_ACTIVATION_EMAIL} component={ResendActivationEmail} />
              <Route
                path={routes.SET_PASSWORD}
                render={props => {
                  return <ResetPassword {...props} isInvitation />;
                }}
              />
              <Route path={routes.LINK_EXPIRED} component={LinkExpired} />
              <Route
                path={routes.SIGN_UP_ORGANIZATION}
                render={props => (
                  <GoogleReCaptchaProvider reCaptchaKey={googleRecaptchaKey}>
                    <SignUpOrganization {...props} />
                  </GoogleReCaptchaProvider>
                )}
              />
              <Route component={NotFound} />
            </Switch>
          </ConditionalRoutes>
          <ConditionalRoutes path={'/'} isPrivate={true}>
            <ContentWrapper>
              <MainNavigation history={history} />
              <Content>
                {user?.emailVerified === false && (
                  <VerifyEmailBar>
                    <RegularText color={colors.white}>
                      {t.VERIFY_EMAIL_MESSAGE} {user?.email}
                    </RegularText>
                    <ResendLink
                      onClick={async () => {
                        const response = await authenticationApi.resendEmailVerificationLink();
                        if (response.ok) {
                          alert(t.EMAIL_VERIFICATION_LINK_RESENT, NotificationMode.SUCCESS);
                        } else {
                          alert(t[getErrorMessage(response as ApiErrorResponse<any>)]);
                        }
                      }}
                    >
                      {t.RESEND}
                    </ResendLink>
                  </VerifyEmailBar>
                )}
                {isCoachSession && <CoachSessionBar user={user} logout={logout} />}
                <Switch>
                  <Route path={routes.CALENDAR} component={CalendarLoader} />
                  <Route path={routes.TASKS} component={TaskListPage} />
                  <Route path={routes.TERM} component={SetupOnboarding} />
                  <Route path={routes.SETTINGS} component={Settings} />
                  <Route path={routes.CHARTS} component={Charts} />
                  <Route path={routes.GPA_CALCULATOR} component={GpaCalculator} />
                  <Route path={routes.AWAKE_TIME} component={props => <AwakeTime {...props} mode={CalendarMode.AWAKE} />} />
                  <Route path={routes.STUDY_TIME} component={StudyTime} />
                  <Route path={routes.BADGES} component={Badges} />
                  <Route path={routes.DEMO} component={OnboardingDemo} />
                  <Route component={NotFound} />
                </Switch>
                <ShovelAssistant />
                <SyllabusImportStatus />
                <UndoShortcut />
              </Content>
            </ContentWrapper>
          </ConditionalRoutes>
        </Switch>
        <ManageTaskDialog />
        <TasksDialog />
        <ConfirmationDialog />
        <LinkGoogleCalToCourseDialog />
        <PersonalizationDialog />
        <ImportCourseSyllabusFilePickerDialog />
        <CreateTaskOverlay />
        <RepeatPatternDialog />
        <NewEstimateDialog />
        <CategoryTimeDialog />
        <IcsUpdatesDialog />
      </Container>
    </ThemeProvider>
  );
};

const mapStateToProps = (state: RootState) => ({
  ...getAppLoaded(state),
  ...getSubscriptionExpired(state),
  ...getVerificationRequired(state),
  ...getIsCoachSession(state),
  user: getCurrentUser(state),
  semesterId: getId(state),
  isParentLink: getParentLink(state),
  icsUpdateTimestamp: getIcsUpdatesTimestamp(state)
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      onAppStart: commonActions.onAppStart,
      getCurrentTimer: timerActions.getCurrentTimer.request,
      checkIcsUpdates: icsActions.checkForIcsUpdates,
      logout: authActions.logout
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(App);

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  width: 100%;
  background-color: ${({ theme }) => theme.backgroundDark};
  transition: background-color 0.2s ease;
  ${isTouchScreen && '.delete-button { visibility: visible !important; }'};

  ${DatePickerStyles}
  ~ * {
    ${DatePickerStyles}
  }

  svg:not(.theme-icon) {
    > *[fill='${rawColors.purple}'] {
      fill: ${({ theme }) => theme.primary};
    }
    > *[fill='${rawColors.darkPurple}'] {
      fill: ${({ theme }) => theme.primaryDark};
    }
  }
`;

const ContentWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: stretch;
`;

const Content = styled(FlexColumn)`
  box-sizing: border-box;
  padding-left: 2px;
`;

const VerifyEmailBar = styled(Row)`
  height: 30px;
  background-color: ${colors.primaryPurple};
  justify-content: center;
`;

const ResendLink = styled(RegularText)`
  display: block;
  margin-left: 15px;
  color: ${colors.white};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;
